import { siteConfig } from '@/config/site.config';
import { supabase } from './supabase';
import type { Cart } from '@/types';

interface CheckoutRequest {
  eventId: string;
  clerkUserId: string;
  userEmail: string;
  userName?: string;
  cart: Cart;
  discountCode?: string;
  successUrl: string;
  cancelUrl: string;
  idempotencyKey?: string;
}

interface CheckoutResponse {
  sessionId: string;
  url: string;
  orderId: string;
}

/**
 * Create a Stripe checkout session via Supabase edge function
 */
export async function createCheckoutSession(params: {
  cart: Cart;
  userEmail: string;
  clerkUserId: string;
  userName?: string;
  discountCode?: string;
}): Promise<CheckoutResponse> {
  const { cart, userEmail, clerkUserId, userName, discountCode } = params;

  // Generate idempotency key to prevent duplicate orders
  const idempotencyKey = `${clerkUserId}-${siteConfig.eventId}-${Date.now()}`;

  const request: CheckoutRequest = {
    eventId: siteConfig.eventId,
    clerkUserId,
    userEmail,
    userName,
    cart,
    discountCode,
    successUrl: `${window.location.origin}/success`,
    cancelUrl: `${window.location.origin}/register`,
    idempotencyKey,
  };

  const response = await fetch(
    `${siteConfig.supabaseUrl}/functions/v1/create-checkout-session`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${siteConfig.supabaseAnonKey}`,
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Checkout failed: ${response.statusText}`);
  }

  return response.json();
}

export interface FreeRegistrationResult {
  orderId: string;
  emailSent: boolean;
  emailError?: string;
}

/**
 * Create a free registration directly in Supabase
 * Used when cart total is 0 (no payment needed)
 * Returns email status so UI can show appropriate feedback
 */
export async function createFreeRegistration(params: {
  clerkUserId: string;
  userEmail: string;
  userName?: string;
  entryType: 'free' | 'spectator' | 'paid';
}): Promise<FreeRegistrationResult> {
  const { clerkUserId, userEmail, userName, entryType } = params;

  // Create the event registration directly
  const { data: registration, error: regError } = await supabase
    .from('event_registrations')
    .upsert(
      {
        tenant_id: siteConfig.tenantId,
        event_id: siteConfig.eventId,
        clerk_user_id: clerkUserId,
        user_email: userEmail,
        user_name: userName,
        entry_type: entryType,
        registration_fee: 0,
        payment_status: 'completed',
      },
      {
        onConflict: 'clerk_user_id,event_id',
      }
    )
    .select()
    .single();

  if (regError) {
    throw new Error(`Failed to create registration: ${regError.message}`);
  }

  // Create a $0 order for record-keeping
  const { data: order, error: orderError } = await supabase
    .from('orders')
    .insert({
      tenant_id: siteConfig.tenantId,
      event_id: siteConfig.eventId,
      clerk_user_id: clerkUserId,
      user_email: userEmail,
      user_name: userName,
      subtotal: 0,
      tax: 0,
      total: 0,
      payment_status: 'completed',
    })
    .select()
    .single();

  if (orderError) {
    console.error('Failed to create order record:', orderError);
    // Don't throw - registration succeeded
  }

  // Send confirmation email for free registration
  // Track email status to inform the UI
  let emailSent = false;
  let emailError: string | undefined;

  try {
    await sendFreeRegistrationEmail({
      tenantId: siteConfig.tenantId,
      eventId: siteConfig.eventId,
      clerkUserId,
      userEmail,
      userName,
      orderId: order?.id,
      registrationId: registration.id,
    });
    emailSent = true;
  } catch (err) {
    emailError = err instanceof Error ? err.message : 'Failed to send email';
    console.error('Failed to send confirmation email:', err);
  }

  return {
    orderId: order?.id || registration.id,
    emailSent,
    emailError,
  };
}

/**
 * Send confirmation email for free registration
 */
async function sendFreeRegistrationEmail(params: {
  tenantId: string;
  eventId: string;
  clerkUserId: string;
  userEmail: string;
  userName?: string;
  orderId?: string;
  registrationId: string;
}): Promise<void> {
  const { tenantId, eventId, clerkUserId, userEmail, userName, orderId, registrationId } = params;

  // Get event info for the email
  const { data: event } = await supabase
    .from('events')
    .select('name, start_date, end_date, location, venue')
    .eq('id', eventId)
    .single();

  const templateData = {
    order_number: (orderId || registrationId).slice(0, 8).toUpperCase(),
    order_total: '$0.00',
    order_items: 'Free Registration',
    event_name: event?.name || 'Event',
    event_dates: event ? formatDateRange(event.start_date, event.end_date) : '',
    event_location: event?.location || event?.venue?.address || '',
  };

  // Send registration confirmation email
  await fetch(`${siteConfig.supabaseUrl}/functions/v1/send-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${siteConfig.supabaseAnonKey}`,
    },
    body: JSON.stringify({
      tenantId,
      eventId,
      templateType: 'registration_confirmation',
      recipientEmail: userEmail,
      recipientName: userName,
      recipientUserId: clerkUserId,
      templateData,
      orderId,
      registrationId,
      registrationType: 'event',
    }),
  });
}

function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };

  if (startDate === endDate) {
    return start.toLocaleDateString('en-US', options);
  }

  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.getDate()}, ${end.getFullYear()}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', options)}`;
  }

  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
}
